<template>
    <div class="sf-image-choice">
        <div v-if="fieldLabel" class="ic-label">
            {{ fieldLabel }}
            <span v-if="isRequired" class="ic-required">*</span>
        </div>

        <div v-if="!options.length" class="ic-empty">Seçenek bulunamadı</div>

        <div v-else class="ic-grid" :class="layoutClass">
            <div
                v-for="opt in options"
                :key="opt.id"
                class="ic-card"
                :class="{ selected: isSelected(opt) }"
                @click="onSelect(opt)"
            >
                <div class="ic-img-wrap">
                    <img
                        v-if="getImageUrl(opt)"
                        :src="getImageUrl(opt)"
                        :alt="opt.label || opt.value"
                        class="ic-img"
                    />
                    <div v-else class="ic-img-placeholder">{{ (opt.label || '?').charAt(0) }}</div>
                    <span class="ic-check" v-if="isSelected(opt)">✓</span>
                </div>
                <div class="ic-card-label">{{ opt.label || opt.value }}</div>
            </div>
        </div>

        <div v-if="config.multiple && (config.minSelections || config.maxSelections)" class="ic-hint">
            <span v-if="config.minSelections && config.maxSelections">
                {{ config.minSelections }} - {{ config.maxSelections }} arası seçin
            </span>
            <span v-else-if="config.minSelections">En az {{ config.minSelections }} seçin</span>
            <span v-else-if="config.maxSelections">En fazla {{ config.maxSelections }} seçin</span>
        </div>

        <div v-if="showError && hasError" class="ic-error">
            {{ errorMessage }}
        </div>
    </div>
</template>

<script>
export default {
    name: "ImageChoice",
    props: {
        value: { type: [Array, String, Object], default: null },
        imageChoice: { type: Object, default: () => ({}) },
        label: { type: String, default: "" },
        required: { type: Boolean, default: false },
        showError: { type: Boolean, default: false },
        formId: { type: String, default: "" },
        fieldId: { type: String, default: "" },
        order: { type: Number, default: 0 },
    },
    computed: {
        config() { return this.imageChoice || {}; },
        fieldLabel() { return this.label || ""; },
        isRequired() { return !!this.required; },
        options() { return Array.isArray(this.config.options) ? this.config.options : []; },
        layoutClass() {
            return "layout-" + (this.config.layout || "grid-3");
        },
        normalizedValue() {
            if (!this.value) return this.config.multiple ? [] : null;
            if (this.config.multiple) {
                return Array.isArray(this.value) ? this.value : [this.value];
            }
            return Array.isArray(this.value) ? this.value[0] : this.value;
        },
        selectionCount() {
            return Array.isArray(this.normalizedValue) ? this.normalizedValue.length : (this.normalizedValue ? 1 : 0);
        },
        hasError() {
            if (!this.isRequired) return false;
            if (this.config.multiple) {
                const min = this.config.minSelections || 1;
                return this.selectionCount < min;
            }
            return !this.normalizedValue;
        },
        errorMessage() {
            if (this.config.multiple && this.config.minSelections && this.selectionCount < this.config.minSelections) {
                return "En az " + this.config.minSelections + " seçim yapmalısınız";
            }
            return "Bu alan zorunludur";
        },
    },
    methods: {
        resolveImageUrl(assetUrl, asset) {
            if (assetUrl) return assetUrl;
            if (!asset) return null;
            if (typeof asset === "string") return asset;
            if (asset.cdnUrl) return asset.cdnUrl;
            if (asset.url) return asset.url;
            if (asset.path && asset._id && asset.ext) {
                return "https://static.supsis.live/" + asset.path + asset._id + "." + asset.ext;
            }
            return null;
        },
        getImageUrl(opt) {
            return this.resolveImageUrl(opt.assetUrl, opt.asset);
        },
        isSelected(opt) {
            const targetVal = opt.value || opt.id;
            if (this.config.multiple) {
                const arr = this.normalizedValue;
                return Array.isArray(arr) && arr.some((v) => String(v) === String(targetVal));
            }
            return String(this.normalizedValue) === String(targetVal);
        },
        onSelect(opt) {
            const val = opt.value || opt.id;
            if (!this.config.multiple) {
                this.$emit("input", val);
                this.$emit("validate", { fieldId: this.fieldId, hasError: !val && this.isRequired });
                return;
            }
            const arr = Array.isArray(this.normalizedValue) ? this.normalizedValue.slice() : [];
            const idx = arr.findIndex((v) => String(v) === String(val));
            if (idx === -1) {
                if (this.config.maxSelections && arr.length >= this.config.maxSelections) return;
                arr.push(val);
            } else {
                arr.splice(idx, 1);
            }
            this.$emit("input", arr);
            this.$emit("validate", { fieldId: this.fieldId, hasError: this.hasError });
        },
    },
};
</script>

<style lang="scss" scoped>
.sf-image-choice {
    display: flex;
    flex-direction: column;
    gap: 8px;
    width: 100%;
}

.ic-label {
    font-weight: 600;
    color: var(--text-primary, #1f2937);
    font-size: clamp(14px, 1.8vw, 16px);
    .ic-required { color: #dc2626; margin-left: 4px; }
}

.ic-empty {
    color: #9ca3af;
    text-align: center;
    padding: 24px;
    background: #f9fafb;
    border-radius: 8px;
    font-size: 13px;
}

.ic-grid {
    display: grid;
    gap: 10px;

    &.layout-grid-2 { grid-template-columns: repeat(2, 1fr); }
    &.layout-grid-3 { grid-template-columns: repeat(3, 1fr); }
    &.layout-grid-4 { grid-template-columns: repeat(4, 1fr); }

    @media (max-width: 640px) {
        grid-template-columns: repeat(2, 1fr) !important;
    }
}

.ic-card {
    border: 2px solid #e5e7eb;
    border-radius: 10px;
    overflow: hidden;
    cursor: pointer;
    transition: border-color 0.15s, transform 0.1s;
    background: #ffffff;
    user-select: none;

    &:hover {
        border-color: var(--primary, #ec4899);
        transform: translateY(-2px);
    }
    &.selected {
        border-color: var(--primary, #ec4899);
        box-shadow: 0 0 0 3px rgba(236, 72, 153, 0.18);
    }

    .ic-img-wrap {
        position: relative;
        aspect-ratio: 4 / 3;
        background: #f3f4f6;
        overflow: hidden;

        .ic-img {
            width: 100%;
            height: 100%;
            object-fit: cover;
            display: block;
        }
        .ic-img-placeholder {
            width: 100%;
            height: 100%;
            display: flex;
            align-items: center;
            justify-content: center;
            font-size: 32px;
            color: #9ca3af;
            font-weight: 700;
        }
        .ic-check {
            position: absolute;
            top: 6px;
            right: 6px;
            width: 22px;
            height: 22px;
            background: var(--primary, #ec4899);
            color: #ffffff;
            border-radius: 50%;
            display: inline-flex;
            align-items: center;
            justify-content: center;
            font-weight: 700;
            font-size: 12px;
            box-shadow: 0 2px 4px rgba(0, 0, 0, 0.15);
        }
    }

    .ic-card-label {
        padding: 8px 10px;
        font-size: 13px;
        color: var(--text-primary, #4b5563);
        text-align: center;
        background: #ffffff;
        border-top: 1px solid #f3f4f6;
    }
}

.ic-hint {
    font-size: 11px;
    color: #6b7280;
}

.ic-error {
    color: #dc2626;
    font-size: 12px;
    margin-top: 4px;
}
</style>
